// src/repositories/recommendationsRepository.js
//
// Data-access layer for product recommendations.
//
// Firestore efficiency considerations:
//  - Candidate retrieval reuses `productRepository.listRecent`, which is a single
//    bounded `getDocs` (no realtime listener) ordered by `createdAt` desc — an
//    index that already exists. No new composite indexes are required.
//  - The candidate list is pooled in a module-level cache (TTL) so that the
//    ProductDetails view and subsequent views in the same session do not re-read
//  - Same-seller alternatives come from the same candidate set, so no extra
//    per-view query is issued. If a seller has no recent listings in the pool,
//    ranking naturally widens to other sellers' relevant products.
//  - Wishlist ids are read once, on demand, only for the authenticated viewer.
//    The wishlist collection is private to the owner (rules) — no other user's
//    activity is ever read or exposed.
//  - Errors degrade gracefully: a failed candidate fetch yields an empty result
//    rather than a crash, and is surfaced to the caller via `error`.

import { productRepository } from './productRepository';
import { wishlistRepository } from './wishlistRepository';
import { errorDev } from '../utils/logger';
import { RECOMMENDATIONS_CANDIDATE_LIMIT } from '../utils/productRecommendations';

const CACHE_TTL_MS = 30_000; // short-lived: marketplace stock changes quickly

let cache = {
  rows: [],
  fetchedAt: 0,
  promise: null,
};

export const recommendationsRepository = {
  /**
   * Bounded, cached candidate pool for ranking.
   * @param {{ max?: number, forceRefresh?: boolean }} [opts]
   * @returns {Promise<object[]>} mapped product rows
   */
  async listCandidates({ max = RECOMMENDATIONS_CANDIDATE_LIMIT, forceRefresh = false } = {}) {
    const safeMax = Math.max(1, Math.min(Number(max) || RECOMMENDATIONS_CANDIDATE_LIMIT, 500));
    const now = Date.now();
    const fresh = cache.rows.length > 0 && now - cache.fetchedAt < CACHE_TTL_MS;

    if (!forceRefresh && fresh && cache.promise == null) {
      return cache.rows;
    }
    if (forceRefresh && cache.promise) {
      // wait for an in-flight refresh to settle before re-fetching
      try { await cache.promise; } catch (e) { /* ignored */ }
    }

    // Coalesce concurrent loaders so a burst of ProductDetails mounts triggers
    // a single Firestore read, not N parallel reads.
    if (cache.promise) return cache.promise;

    const promise = productRepository
      .listRecent({ max: safeMax })
      .catch((err) => {
        errorDev('recommendationsRepository: candidate load failed', err);
        return [];
      });

    cache.promise = promise;
    try {
      const rows = await promise;
      cache = { rows, fetchedAt: Date.now(), promise: null };
      return rows;
    } catch (err) {
      cache = { rows: [], fetchedAt: Date.now(), promise: null };
      return [];
    }
  },

  /**
   * One-time read of the viewer's wishlist product ids (save history), used to
   * boost products the buyer has already signaled interest in. Returns an empty
   * array when unauthenticated or when the read fails.
   * @param {string} userId
   * @returns {Promise<string[]>}
   */
  async listWishlistProductIds(userId) {
    if (!userId) return [];
    try {
      const entries = await wishlistRepository.listAll(userId);
      return entries
        .map((entry) => entry?.product?.id || entry?.productId)
        .filter(Boolean);
    } catch (err) {
      errorDev('recommendationsRepository: wishlist load failed', err);
      return [];
    }
  },

  /** Invalidate the candidate cache (used by tests and after a new listing). */
  clearCandidateCache() {
    cache = { rows: [], fetchedAt: 0, promise: null };
  },
};

export default recommendationsRepository;
